import React, { useCallback } from 'react';
import icons from '@components/dataDisplay/Icons/Icons';
import Color from '@styles/Color';
import styled, { css } from 'styled-components';
import Tooltip from '@components/dataDisplay/Tooltip';
import { motion } from 'framer-motion';
import { TOOLTIP_DIR_TYPE_VALUE } from '@typings/common/tooltipDirType';

type Size = 'xl' | 'lg' | 'md' | 'sm' | 'xs';

const buttonShapeMap = {
  default: css`
    background: transparent;
    path {
      fill: var(--textColor);
    }

    :hover {
      background: var(--inkColor);
      path {
        fill: var(--textColor);
      }
    }

    :disabled {
      opacity: 0.5;
    }
  `,
  stroke: css`
    color: var(--textColor);
    background-color: var(--cardBackgroundColor);
    border: 1px solid var(--lineColor);
    backdrop-filter: blur(4px);

    :hover {
      background-color: var(--inkColor);
    }
    :disabled {
      opacity: 0.6;
    }
  `,
  fill: css`
    background-color: var(--buttonBackgroundColor);
    backdrop-filter: blur(4px);
    color: var(--buttonTextColor);

    path {
      fill: var(--buttonTextColor);
    }

    :hover {
      background-color: var(--inkColor);
      path {
        fill: var(--buttonTextColor);
      }
    }

    :disabled {
      background-color: var(--buttonBackgroundColor);
      path {
        fill: var(--subTextColor);
      }
    }
  `,
};

const ButtonWrap = styled(motion.button) <{
  size: '40px' | '36px' | '32px' | '24px' | '18px';
  shape?: keyof typeof buttonShapeMap;
  hoverColor?: string;
  iconColor?: string;
}>`
  display: flex;
  align-items: center;
  padding: 0;
  justify-content: center;
  outline: none;
  box-shadow: none;
  white-space: nowrap;
  border: ${(props) =>
    props.shape === 'stroke' ? `1px solid ${Color.BLUE_GRAY_3}` : 'none'};
  cursor: var(--hoverCursorURL), pointer;
  width: ${({ size }) => size};
  min-width: ${({ size }) => size};
  height: ${({ size }) => size};
  min-height: ${({ size }) => size};
  transition: 0.2s;
  border-radius: var(--buttonBorderRadius);
  user-select: none;
  ${(props) => buttonShapeMap[props.shape!]};

  :hover {
    background: ${({ hoverColor }) => hoverColor};
  }

  path {
    fill: ${({ iconColor }) => iconColor || 'var(--textColor)'};
  }
`;

interface Props {
  size?: Size;
  shape?: keyof typeof buttonShapeMap;
  style?: React.CSSProperties;
  icon?: keyof typeof icons;
  onClick?: (e: any) => void;
  disabled?: boolean;
  hoverColor?: string;
  iconColor?: string;
  tooltip?: string;
  children?: any;
  tooltipDir?: TOOLTIP_DIR_TYPE_VALUE;
}

function IconButton({
  size = 'lg',
  shape = 'stroke',
  style,
  icon,
  onClick,
  disabled,
  hoverColor,
  iconColor,
  tooltip,
  children,
  tooltipDir,
}: Props) {
  const ButtonSize =
    size === 'xl'
      ? '40px'
      : size === 'lg'
        ? '36px'
        : size === 'md'
          ? '32px'
          : size === 'sm'
            ? '24px'
            : '18px';

  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      if (onClick) onClick(e);
    },
    [onClick],
  );

  return tooltip ? (
    <Tooltip message={tooltip} direction={tooltipDir || 'bottom'}>
      <ButtonWrap
        size={ButtonSize}
        style={style}
        shape={shape}
        disabled={disabled}
        hoverColor={hoverColor}
        iconColor={iconColor}
        onClick={handleClick}
      >
        {icon && icons[icon]}
        {children}
      </ButtonWrap>
    </Tooltip>
  ) : (
    <ButtonWrap
      size={ButtonSize}
      style={style}
      shape={shape}
      disabled={disabled}
      hoverColor={hoverColor}
      iconColor={iconColor}
      onClick={handleClick}
    >
      {icon && icons[icon]}
      {children}
    </ButtonWrap>
  );
}

export default React.memo(IconButton);
